import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Button, Input, Loading } from '@ibk/shared-ui';
import { isValidCpf, maskCpf } from '@ibk/shared-utils';
import { hasRequiredOnboardingContacts } from '../../hasRequiredOnboardingContacts';
import { useAccountData } from '../hooks';
import styles from './CpfValidation.module.css';
import { useCpfValidation } from './hooks';
export const ONBOARDING_ACCESS_TOAST_KEY = 'onboarding_show_access_toast';
export function CpfValidation({ domain, isContinuing = false, onBackToLogin, onContinue }) {
    const { t } = useTranslation();
    const { cpf: persistedCpf, email, phone, setCpf: setPersistedCpf, setAccount, setFullName } = useAccountData();
    const [cpf, setCpf] = useState(() => persistedCpf ?? '');
    const [cpfTouched, setCpfTouched] = useState(false);
    const lastPersistedCpfRef = useRef(persistedCpf);
    useEffect(() => {
        if (persistedCpf !== lastPersistedCpfRef.current && persistedCpf !== cpf) {
            setCpf(persistedCpf);
            lastPersistedCpfRef.current = persistedCpf;
        }
    }, [persistedCpf, cpf]);
    const setPersistedCpfRef = useRef(setPersistedCpf);
    useEffect(() => {
        setPersistedCpfRef.current = setPersistedCpf;
    }, [setPersistedCpf]);
    useEffect(() => {
        if (cpf !== persistedCpf) {
            lastPersistedCpfRef.current = cpf;
            setPersistedCpfRef.current(cpf);
        }
    }, [cpf, persistedCpf]);
    const cpfInvalidError = cpfTouched && cpf && !isValidCpf(cpf) ? t('onboarding.cpf-invalid') : undefined;
    const isValid = Boolean(cpf && isValidCpf(cpf));
    const { data, isLoading, hasValidAccount } = useCpfValidation({ cpf, enabled: isValid });
    const lastProcessedAccountIdRef = useRef(undefined);
    const setAccountRef = useRef(setAccount);
    const setFullNameRef = useRef(setFullName);
    useEffect(() => {
        setAccountRef.current = setAccount;
        setFullNameRef.current = setFullName;
    }, [setAccount, setFullName]);
    useEffect(() => {
        if (!data?.result) {
            return;
        }
        let apiAccount;
        if (Array.isArray(data.result)) {
            if (data.result.length === 0) {
                return;
            }
            apiAccount = data.result[0];
        }
        else {
            apiAccount = data.result;
        }
        if (!apiAccount || typeof apiAccount !== 'object' || apiAccount === null) {
            return;
        }
        const account = apiAccount;
        const isAccountCreated = account.has_access ?? false;
        if (isAccountCreated) {
            sessionStorage.setItem(ONBOARDING_ACCESS_TOAST_KEY, 'true');
            setCpf('');
            setPersistedCpfRef.current('');
            onBackToLogin();
            return;
        }
        const accountId = account.id ?? account.email;
        const accountKey = accountId ?? JSON.stringify(account);
        if (accountKey && accountKey !== lastProcessedAccountIdRef.current) {
            lastProcessedAccountIdRef.current = accountKey;
            const fullName = account.full_name ?? account.fullName ?? '';
            const email = account.email ?? '';
            const phoneNumber = account.phoneNumber ?? account.phone_number ?? '';
            const transformedAccount = {
                ...account,
                email,
                phoneNumber,
            };
            setAccountRef.current(transformedAccount);
            setFullNameRef.current(fullName);
        }
    }, [data, onBackToLogin]);
    const title = domain === 'escritorio' ? t('onboarding.title.escritorio') : t('onboarding.title.empresa');
    const showAccountError = isValid && !isLoading && data !== undefined && !hasValidAccount;
    const showMissingContactError = isValid && !isLoading && hasValidAccount && !hasRequiredOnboardingContacts(email, phone);
    const cpfError = showAccountError ? t('onboarding.error.cpf-not-found') : cpfInvalidError;
    const handleContinue = async () => {
        if (!hasValidAccount || isContinuing || showMissingContactError) {
            return;
        }
        await onContinue();
    };
    return (_jsxs("div", { className: styles.cpfValidationWrapper, "data-testid": 'cpf-validation-wrapper', children: [_jsx("h1", { className: styles.cpfValidationTitle, "data-testid": 'cpf-validation-title', children: title }), _jsx("p", { className: styles.cpfValidationSubtitle, "data-testid": 'cpf-validation-subtitle', children: t('onboarding.subtitle') }), _jsxs("div", { className: styles.cpfValidationField, "data-testid": 'cpf-validation-field', children: [_jsx("div", { className: styles.cpfValidationInputWrapper, "data-testid": 'cpf-validation-input-wrapper', children: _jsx(Input, { id: 'onboarding-cpf', name: 'onboarding-cpf', type: 'text', inputMode: 'numeric', placeholder: '000.000.000-00', value: cpf, onChange: (e) => setCpf(maskCpf(e.target.value)), onBlur: () => setCpfTouched(true), required: true, maxLength: 14, autoComplete: 'off', dataTestId: 'cpf-validation-input-cpf', size: 'large', label: t('onboarding.label-cpf'), error: cpfError }) }), showMissingContactError ? (_jsx("p", { className: styles.cpfValidationContactError, "data-testid": 'cpf-validation-missing-contact-error', children: t('onboarding.error.missing-valid-contact') })) : null] }), _jsxs("div", { className: styles.cpfValidationActions, "data-testid": 'cpf-validation-actions', children: [_jsxs(Button, { variant: 'primary', size: 'large', type: 'button', onClick: handleContinue, disabled: !cpf ||
                            !!cpfInvalidError ||
                            isLoading ||
                            !hasValidAccount ||
                            showAccountError ||
                            showMissingContactError ||
                            isContinuing, dataTestId: 'cpf-validation-continue', children: [isLoading || isContinuing ? _jsx(Loading, { size: 'micro', style: { marginRight: 'var(--spacing-1, 8px)' } }) : null, t('onboarding.button-continue')] }), _jsx(Button, { variant: 'tertiary', size: 'large', type: 'button', onClick: onBackToLogin, dataTestId: 'cpf-validation-back-to-login', children: t('onboarding.button-back-to-login') })] })] }));
}
